import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Input, Table, Tag, Typography, Modal, Tooltip } from 'antd';
import { getColumnSearchProps } from '../../../approvalsBonus/presentation/filterSearch.jsx';
import NoDataState from '../../../RnRCommonComponent/noDataState/NoDataState.jsx';
import uuid from 'uuid/v4';
import { useSelector } from 'react-redux';
import { addCommas } from '../../../rewardsAndRecognitionHelper/utils.js';
import { CoreBadge } from '../../../RnRCommonComponent';
import { REVERSE_MAPING_BADGE_TYPE, ICON_COMPLETE_MAP } from '../../../RnRCommonComponent/CoreBadge/rewardsConstants.js';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

const ApprovedContainer = (props) => {
	const {
		tableData,
		isPending = false,
		isApprove,
		isDecline,
		setIsDecline,
		setIsApprove,
		handleApprovalClick,
		handleDeclineClick,
		handleApproveAndDeclined,
		pendingRecordId,
		pendingRecord,
		getJustificationContent
	} = props;
	const currentUser = useSelector((state) => state.user.currentUser);
	const recognitionSettings = currentUser?.company?.recognitionSettings || {};
	const {
		assignSnappyRewardToRecognition = false,
		rewardsEarnedMechanism = 'milestone',
		baseSnappyBudget = 0
	} = recognitionSettings;
	const [showJustificationModal, setShowJustificationModal] = useState(false);
	const [selectedJustification, setSelectedJustification] = useState([]);
	const [showShoutoutModal, setShowShoutoutModal] = useState(false);
	const [selectedShoutout, setSelectedShoutout] = useState(null);
	const [declineReason, setDeclineReason] = useState('');
	
	const searchInput = useRef(null);

	const handleSearch = (_selectedKeys, confirm) => {
		confirm();
	};

	const handleReset = (clearFilters) => {
		clearFilters();
	};

	const statusColorMap = (s, o = 1) => (
		s === 'Approved'
			? `oklch(0.7903 0.136 192.97 / ${o})`
			: s === 'Declined'
				? `oklch(0.58 0.26 295 / ${o})`
				: `oklch(0.6757 0.187 349.98 / ${o})`
	);
	const columns = [
		{
			title: 'Recipient',
			dataIndex: 'user',
			width: 180,
			key: 'recipient',
			render: (user = {}) => (
				`${user?.firstName} ${user?.lastName}`
			),
			...getColumnSearchProps(
				'recipient',
				searchInput,
				handleSearch,
				handleReset,
				'Recipient',
				true
			),
		},
		...(rewardsEarnedMechanism === 'milestone' ? [
			{
				title: 'Points',
				dataIndex: 'badge',
				width: 80,
				key: 'points',
				render: (badge = {}) => (
					<span style={{ color: 'green' }}>
						{addCommas(badge?.points || 0)}
					</span>
				),
			},
			{
				title: 'Bonus Points',
				dataIndex: 'badge',
				width: 100,
				key: 'bonusPoints',
				render: (badge = {}) => (
					<span style={{ color: badge?.bonusPoints > 0 ? 'orange' : 'inherit' }}>
						{badge?.bonusPoints ? `+${addCommas(badge?.bonusPoints)}` : 0}
					</span>
				),
			}
		] : [
			{
				title: 'Amount',
				dataIndex: 'badge',
				width: 80,
				key: 'costInCurrency',
				render: (badge = {}) => (
					<span style={{ color: badge?.snappyGrant?.costInCurrency === 0 ? 'inherit' : 'green' }}>
						{'$' + (badge?.snappyGrant?.costInCurrency ?? 0)}
					</span>
				),
			},
			{
				title: 'Budget',
				dataIndex: 'user',
				width: 80,
				key: 'remainingBudget',
				render: (user = {}) => {
					const currentBudget = user?.recognitionEvents?.currentBudget;
					const remainingBudget = currentBudget !== undefined ? currentBudget : baseSnappyBudget;
					return (
					<span style={{ color: remainingBudget <= 0 ? 'red' : 'green' }}>
						{'$' + addCommas(remainingBudget)}
					</span>
				)},
			}
		]),
		{
			title: 'Recognition',
			dataIndex: 'badge',
			width: 180,
			key: 'badgeName',
			render: (badge = {}) => (badge?.name || ""),
		},
		...(recognitionSettings.assignSnappyRewardToRecognition || rewardsEarnedMechanism !== 'milestone' ? [{
			title: 'Reward',
			dataIndex: 'badge',
			width: 140,
			key: 'reward',
			render: (badge = {}) => (badge?.snappyGrant?.name || ""),
		}] : []),
		{
			title: 'Created By',
			dataIndex: 'awardedByUser',
			width: 150,
			key: 'createdBy',
			render: (awardedByUser = {}, record) => awardedByUser
				? `${awardedByUser.firstName || ''} ${awardedByUser.lastName || ''}`.trim()
				: (record.awardedBy || 'Unknown'),
		},
		{
			title: 'Created',
			dataIndex: 'createdAt',
			key: 'created',
			defaultSortOrder: 'descend',
			sorter: (a, b) => new Date(a?.createdAt) - new Date(b?.createdAt),
			render: (createdAt, record) => createdAt ? dayjs(createdAt).format('MM/DD/YYYY') : record?.updatedAt ? dayjs(record.updatedAt).format('MM/DD/YYYY') : 'Unknown',
		},
		...(!isPending ? [{
			title: 'Approved',
			dataIndex: 'completedAt',
			key: 'completed',
			render: (_completedAt, record) => record.completedAt ? dayjs(record.completedAt).format('MM/DD/YYYY') : record.updatedAt ? dayjs(record.updatedAt).format('MM/DD/YYYY') : 'Unknown',
		}] : []),
		{
			title: rewardsEarnedMechanism === 'milestone' ? 'Shoutout' : 'Answers',
			dataIndex: rewardsEarnedMechanism === 'milestone' ? 'content' : 'justificationFormAnswers',
			key: rewardsEarnedMechanism === 'milestone' ? 'shoutout' : 'justification',
			render: (_data, record) => {
				if (rewardsEarnedMechanism === 'milestone') {
					const hasContent = record?.content?.message || record?.content?.gifURL;
					return hasContent ? (
						<Button
							size="small"
							type="default"
							className="approve-btn mr-2"
							onClick={() => {
								setSelectedShoutout({
									content: record?.content,
									badgeName: record?.badge?.name,
									recipient: `${record?.user?.firstName || ''} ${record?.user?.lastName || ''}`,
									createdBy: record?.awardedByUser
										? `${record?.awardedByUser?.firstName || ''} ${record?.awardedByUser?.lastName || ''}`.trim()
										: (record?.awardedBy || 'Unknown'),
									created: record?.createdAt ? dayjs(record?.createdAt).format('MM/DD/YYYY') : record?.updatedAt ? dayjs(record?.updatedAt).format('MM/DD/YYYY') : "Unknown",
									points: record?.badge?.points ?? 0,
									bonusPoints: record?.badge?.bonusPoints ?? 0,
									badge: record?.badge,
									rawData: record
								});
								setShowShoutoutModal(true);
							}}
						>
							View
						</Button>
					) : (
						<Paragraph ellipsis={{ rows: 2 }}>
							No message included
						</Paragraph>
					);
				} else {
					const justificationContent = getJustificationContent(record.justificationFormAnswers);
					return justificationContent.length > 0 ? (
						<Button
							size="small"
							type="default"
							className="approve-btn mr-2"
							onClick={() => {
								setSelectedJustification(justificationContent);
								setShowJustificationModal(true);
							}}
						>
							View
						</Button>
					) : (
						<Paragraph ellipsis={{ rows: 2 }}>
							No justification provided
						</Paragraph>
					);
				}
			},
		},
		...(!isPending ? [{
			title: 'Status',
			dataIndex: 'approvalStatus',
			key: 'status',
			render: (approvalStatus, record) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<Tag
						style={{
							color: statusColorMap(approvalStatus),
							borderColor: statusColorMap(approvalStatus, 0.4),
							backgroundColor: statusColorMap(approvalStatus, 0.04)
						}}
					>
						{approvalStatus ?? 'Pending'}
					</Tag>
					{record?.declineReason && 
						<Tooltip arrowPointAtCenter placement="topRight" title={record.declineReason}>
							<i
								className="icon-message-outline"
								alt=""
								style={{ color: 'var(--silver-chalice)', fontSize: 18 }}
							/>
						</Tooltip>
					}
				</div>
			),
		}] : []),
		...(isPending ? [{
			title: 'Approval',
			key: 'approval',
			render: (_, record) => (
				<div style={{ display: 'flex', gap: '8px' }}>
					<Button
						size={"small"}
						type={'primary'}
						className="approve-btn"
						onClick={() => {
							handleApprovalClick(record);
						}}
					>
						Approve
					</Button>
					<Button
						size={"small"}
						type={'danger'}
						className="decline-btn"
						onClick={() => {
							handleDeclineClick(record);
						}}
					>
						Decline
					</Button>
				</div>
			),		
		}] : [])
	];

	return (
		<div className="mt-4">
			<div className="page-title">
				<h2 className="page-heading-manage-reward">{isPending ? "Pending Approval" : "Approval Status"}</h2>
			</div>
			<div className="nomination-rewards-table table-card">
				<Table
					rowKey={(record) => uuid() + record.id}
					dataSource={tableData}
					columns={columns}
					rowHoverable={false}
					rowSelectedHoverBg={"white"}
					pagination={{ pageSize: 25, showSizeChanger: false }}
					locale={{ emptyText: <NoDataState title="You have not approved any recognitions yet." /> }}
				/>
			</div>
			<Modal
				destroyOnClose
				width={420}
				open={isApprove}
				title="Confirm Approval"
				footer={false}
				className="confirmation-modal"
				onOk={false}
				onCancel={() => {
					setIsApprove(false);
				}}
			>
				<>
					<p className="text-center modal-detail">
						Are you sure you want to approve?
					</p>
					<div className="text-center">
						<Button
							className="approve-btn"
							size={"large"}
							type="primary"
							onClick={() =>
								handleApproveAndDeclined(
									pendingRecordId,
									'Approved',
									pendingRecord
								)
							}
						>
							Approve
						</Button>
					</div>
				</>
			</Modal>
			<Modal
				destroyOnClose
				width={420}
				title="Decline Approval"
				open={isDecline}
				footer={false}
				className="confirmation-modal"
				onOk={false}
				onCancel={() => {
					setIsDecline(false);
				}}
			>
				<>
					<p className="text-center modal-detail">
						Are you sure you want to decline?
					</p>
					<div className="text-center modal-detail">
						<TextArea
							maxLength={255}
							placeholder="Please Provide a Reason"
							value={declineReason}
							onChange={(e) => {
								setDeclineReason(e.target.value);
							}}
							className="custom-input"
						/>
					</div>
					<div className="text-center">
						<Button
							className="decline-btn"
							disabled={!declineReason}
							type="primary"
							danger={true}
							onClick={() => {
								handleApproveAndDeclined(
									pendingRecordId,
									'Declined',
									null,
									declineReason
								);
							}}
						>
							Decline
						</Button>
					</div>
				</>
			</Modal>
			<Modal
				destroyOnClose
				width={420}
				open={showJustificationModal}
				title="Justification Answers"
				footer={false}
				className="confirmation-modal"
				onOk={false}
				onCancel={() => setShowJustificationModal(false)}
			>
				
					<div style={{padding: '0 12px', fontSize: '16px'}}>
						{selectedJustification && Array.isArray(selectedJustification) ? selectedJustification.map((j, i) => (
							(
								<div key={i}>
									<span>
										<strong>{j?.question ? j.question + ':' : 'Question:'}</strong>
									</span>
									<p>
										{j?.answer || 'No answer provided'}
									</p>
								</div>
							)
						)) : (
							<p>No justification data available</p>
						)}
					</div>
				
			</Modal>
			<Modal
				destroyOnClose
				width={600}
				open={showShoutoutModal}
				title="Recognition Shoutout"
				footer={false}
				className="confirmation-modal"
				onOk={false}
				onCancel={() => setShowShoutoutModal(false)}
			>
				<div style={{ padding: '20px 0' }}>
					<div style={{
						display: 'flex',
						alignItems: 'center',
						padding: '16px',
						backgroundColor: '#f8f9fa',
						borderRadius: '8px',
						marginBottom: '20px'
					}}>
						{selectedShoutout?.badge?.id && (
							<div className="feed-reward-logo" style={{ marginRight: '16px' }}>
								<CoreBadge
									useTilt={false}
									badgeType={REVERSE_MAPING_BADGE_TYPE[selectedShoutout.badge.badgeTypeId]}
									label={selectedShoutout.badge.label || selectedShoutout.badge.name || 'Badge'}
									iconCode={ICON_COMPLETE_MAP.find(icon => icon.id === selectedShoutout.badge.iconId)?.code}
									color={selectedShoutout.badge.color}
									iconColor={selectedShoutout.badge.iconColor}
									gradientColor={selectedShoutout.badge.gradientColor}
									hideSnappyIcon={true}
								/>
							</div>
						)}
						<div style={{ flex: 1 }}>
							<div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>
								{selectedShoutout?.createdBy} gave {selectedShoutout?.recipient}
							</div>
							<div style={{ fontSize: '16px', color: '#666', marginBottom: '4px' }}>
								{selectedShoutout?.badgeName}
							</div>
							<div style={{ fontSize: '14px', color: '#999', display: 'flex', alignItems: 'center', gap: '12px' }}>
								<span>{dayjs(selectedShoutout?.created).format('MMM DD, YYYY')}</span>
								{selectedShoutout?.points && (
									<span>• {addCommas(selectedShoutout?.points)} points</span>
								)}
								{selectedShoutout?.bonusPoints && (
									<span>• +{addCommas(selectedShoutout?.bonusPoints)} bonus</span>
								)}
							</div>
						</div>
					</div>
					{selectedShoutout?.message ? (
						<div style={{
							marginBottom: '20px',
							padding: '16px',
							backgroundColor: 'white',
							border: '1px solid #e0e0e0',
							borderRadius: '8px'
						}}>
							<Text style={{
								fontSize: '16px',
								lineHeight: '1.6',
								fontStyle: 'italic'
							}}>
								"{selectedShoutout.message}"
							</Text>
						</div>
					) : (
						<div style={{
							marginBottom: '20px',
							padding: '16px',
							backgroundColor: '#f5f5f5',
							borderRadius: '8px',
							textAlign: 'center'
						}}>
							<Text style={{ fontSize: '16px', color: '#666', fontStyle: 'italic' }}>
								No message included
							</Text>
						</div>
					)}
					{selectedShoutout?.gifURL && (
						<div style={{ textAlign: 'center' }}>
							<img
								src={selectedShoutout.gifURL}
								alt="Shoutout GIF"
								style={{
									maxWidth: '100%',
									maxHeight: '300px',
									borderRadius: '8px',
									boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
								}}
							/>
						</div>
					)}
				</div>
			</Modal>
		</div>
	);
};

export default ApprovedContainer;
